import React from 'react';

/**
 * Servisfy logo component. Reads the logo from /public so it works in dev & build.
 * Variants:
 *  - "full"   → logo mark + wordmark image (default)
 *  - "mark"   → just the icon mark (used in tight spaces, sidebars)
 *
 * Size note: sm–xl use height-driven sizing (h-* w-auto) so the mark and full
 * variants stay proportional in constrained spaces (sidebars, headers).
 * xxl uses width-driven sizing for the full variant (w-* h-auto) to prevent the
 * height + max-w combination from squashing object-contain rendering on hero use.
 */
export const ServisfyLogo = ({ variant = 'full', className = '', size = 'md' }) => {
  const sizes = {
    sm:  variant === 'full' ? 'h-8 w-auto'                    : 'h-8 w-8',
    md:  variant === 'full' ? 'h-10 w-auto'                   : 'h-10 w-10',
    lg:  variant === 'full' ? 'h-14 w-auto'                   : 'h-14 w-14',
    xl:  variant === 'full' ? 'h-20 w-auto'                   : 'h-20 w-20',
    // login/landing hero — width-driven so height is always a true aspect-ratio result
    xxl: variant === 'full' ? 'w-48 md:w-56 lg:w-64 h-auto'  : 'h-24 w-24 md:h-28 md:w-28',
  };

  const cls = `${sizes[size] || sizes.md} object-contain ${className}`;

  // Full wordmark logo: theme-aware asset selection via <picture> (the standard HTML
  // mechanism, not a CSS filter/blend trick). The default artwork has a dark-teal wordmark
  // for light surfaces; a dark-surface variant (same symbol + proportions, wordmark recoloured
  // to the approved dark-mode foreground #EAF4F4, transparency preserved) is used when the
  // viewer prefers a dark colour scheme — which is exactly how the public page themes itself.
  if (variant === 'full') {
    return (
      <picture>
        <source srcSet="/servisfy-logo-dark.png" media="(prefers-color-scheme: dark)" />
        <img
          src="/servisfy-logo.png"
          alt="Servisfy"
          data-testid="servisfy-logo-full"
          className={cls}
          draggable={false}
        />
      </picture>
    );
  }

  return (
    <img
      src="/favicon.png"
      alt="Servisfy"
      data-testid={`servisfy-logo-${variant}`}
      className={cls}
      draggable={false}
    />
  );
};

export default ServisfyLogo;
